// Date formatting utilities
export const formatDate = (dateString, options = {}) => {
  if (!dateString) return '-';
  
  const defaultOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: 'Asia/Kolkata',
    ...options
  };
  
  return new Date(dateString).toLocaleDateString('en-IN', defaultOptions);
};

export const formatDateTime = (dateString) => {
  if (!dateString) return '-';
  
  return new Date(dateString).toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  });
};

export const formatDateTimeObject = (dateString) => {
  if (!dateString) return { date: '-', time: '-' };
  
  const date = new Date(dateString).toLocaleDateString('en-IN', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
  
  const time = new Date(dateString).toLocaleTimeString('en-IN', {
    timeZone: 'Asia/Kolkata',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
  
  return { date, time };
};

// Number formatting utilities
export const formatCurrency = (amount, currency = '₹') => {
  if (amount === null || amount === undefined) return '-';
  return `${currency}${Number(amount).toLocaleString('en-IN')}`;
};

export const formatNumber = (number) => {
  if (number === null || number === undefined) return '-';
  return Number(number).toLocaleString('en-IN');
};

// String utilities
export const capitalize = (str) => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const truncateText = (text, maxLength = 50) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

// URL utilities
export const getMediaUrl = (imagePath) => {
  if (!imagePath) return '/assets/media/avatars/300-1.jpg';
  const mediaUrl = process.env.REACT_APP_MEDIA_URL || '';
  return `${mediaUrl}${imagePath}`;
};

// Validation utilities
export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const isValidPhone = (phone) => {
  const phoneRegex = /^[+]?[\d\s\-()]{10,}$/;
  return phoneRegex.test(phone);
};

// Array utilities
export const groupBy = (array, key) => {
  return array.reduce((result, currentValue) => {
    (result[currentValue[key]] = result[currentValue[key]] || []).push(currentValue);
    return result;
  }, {});
};

export const sortBy = (array, key, direction = 'asc') => {
  return [...array].sort((a, b) => {
    const aVal = a[key];
    const bVal = b[key];
    
    if (direction === 'asc') {
      return aVal > bVal ? 1 : -1;
    } else {
      return aVal < bVal ? 1 : -1;
    }
  });
};

// Local storage utilities
export const setLocalStorage = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error('Error setting localStorage:', error);
  }
};

export const getLocalStorage = (key, defaultValue = null) => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch (error) {
    console.error('Error getting localStorage:', error);
    return defaultValue;
  }
};

export const removeLocalStorage = (key) => {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.error('Error removing localStorage:', error);
  }
};

// Debounce utility
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// File utilities
export const getFileExtension = (filename) => {
  return filename.slice((filename.lastIndexOf('.') - 1 >>> 0) + 2);
};

export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Status utilities
export const getStatusBadgeClass = (status) => {
  const statusClasses = {
    active: 'badge-light-success',
    inactive: 'badge-light-danger',
    pending: 'badge-light-warning',
    completed: 'badge-light-success',
    failed: 'badge-light-danger',
    verified: 'badge-light-success',
    unverified: 'badge-light-warning',
    blocked: 'badge-light-danger',
    unblocked: 'badge-light-success'
  };
  
  return statusClasses[status?.toLowerCase()] || 'badge-light-secondary';
};

// Permission utilities (matching Laravel Helper logic)
export const hasPermission = (user, module, subModule, action) => {
  if (!user) return false;
  
  // Admin has all permissions
  if (user.role === 'admin') return true;
  
  // Check specific permissions
  const userPermissions = user.permissions || {};
  return userPermissions[module]?.[subModule]?.[action] || false;
};

export const hasService = (user, service) => {
  if (!user) return false;
  if (user.role === 'admin') return true;
  return user.services?.includes(service) || false;
};

export const hasRole = (user, roles) => {
  if (!user) return false;
  const userRole = user.role;
  return Array.isArray(roles) ? roles.includes(userRole) : roles === userRole;
};